import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    addReaction,
    createMessage,
    deleteMessage,
    listMessages,
    removeReaction,
    updateMessage,
    updateReadCursor,
} from '../../../service/ChatService';
import {mergeMessages, reconcileMessagePage} from '../chatUtils';
import {highestServerSequence, isServerMessage} from '../serverMessages';
import useReadAcknowledgement from './useReadAcknowledgement';
import useMessageOutbox from './useMessageOutbox';
import useMessageMutations from './useMessageMutations';
import {createRefreshQueue} from '../refreshQueue';

export default function useConversationTimeline({
    conversation,
    currentUser,
    invalidation,
    messageMutationEvents,
    refreshConversations,
    readConversationMessages,
    cacheConversationMessages,
    onForbidden,
    cacheScope,
}) {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [error, setError] = useState(null);
    const [atBottom, setAtBottom] = useState(true);
    const [newMessageNotice, setNewMessageNotice] = useState(false);
    const timelineRef = useRef(null);
    const atBottomRef = useRef(true);
    const messagesRef = useRef([]);
    const conversationId = conversation?.id || 0;
    const timelineScope = `${currentUser?.id || 0}:${conversationId}`;
    const owner = useMemo(() => ({key: timelineScope, mounted: true}), [timelineScope]);
    const activeScope = useRef(owner);
    activeScope.current = owner;
    useEffect(() => { owner.mounted = true; return () => { owner.mounted = false; }; }, [owner]);
    const messagesScope = useRef(owner);
    const refreshRequest = useRef(0);
    const [initialSnapshotOwner, setInitialSnapshotOwner] = useState(null);
    const outbox = useMessageOutbox({conversation, currentUser, onForbidden, confirmedMessages: messages, cacheScope});
    const sendOutbox = outbox.send;
    const acknowledge = useReadAcknowledgement(`${currentUser?.id || 0}:${conversationId}`,
        (target, signal) => updateReadCursor(conversationId, target, signal), refreshConversations, setError);
    const highest = messagesScope.current === owner ? highestServerSequence(messages) : 0;
    const serverRead = conversation?.last_read_root_sequence || 0;

    useEffect(() => {
        messagesRef.current = messages;
    }, [messages]);

    const scrollToBottom = useCallback(() => {
        const timeline = timelineRef.current;
        if (timeline) timeline.scrollTop = timeline.scrollHeight;
        atBottomRef.current = true;
        setAtBottom(true);
        setNewMessageNotice(false);
    }, []);

    const refreshMessages = useCallback(async (preserve = true, signal) => {
        if (!conversationId) return [];
        const generation = ++refreshRequest.current;
        const afterSequence = preserve ? highestServerSequence(messagesRef.current) : 0;
        const payload = await listMessages(conversationId, {afterSequence, signal});
        const incoming = [...(payload.messages || [])];
        const page = payload.messages || [];
        if (signal?.aborted || activeScope.current !== owner || !owner.mounted || generation !== refreshRequest.current) return incoming;
        messagesScope.current = owner;
        setMessages(current => {
            const next = preserve ? mergeMessages(current, incoming) : reconcileMessagePage(current, incoming);
            void cacheConversationMessages?.(conversationId, next.filter(isServerMessage));
            return next;
        });
        if (!preserve) setHasMore(page.length === 50);
        return incoming;
    }, [cacheConversationMessages, conversationId, owner]);

    useEffect(() => {
        if (!conversationId) {
            setMessages([]);
            return;
        }
        let active = true;
        const abortController = new AbortController();
        const initialQueue = createRefreshQueue(async (_, signal) => {
            const page = await refreshMessages(false, signal);
            if (active && !signal.aborted) { setInitialSnapshotOwner(owner); setError(null); setLoading(false); window.requestAnimationFrame(scrollToBottom); }
            return page;
        }, () => true, {retry: true});
        messagesScope.current = owner;
        setInitialSnapshotOwner(null);
        setMessages([]);
        setLoading(true);
        setError(null);
        atBottomRef.current = true;
        setAtBottom(true);
        setNewMessageNotice(false);
        const cached = readConversationMessages?.(conversationId) || Promise.resolve([]);
        cached.then(cachedMessages => {
            if (active && cachedMessages.length) {
                setMessages(cachedMessages.filter(isServerMessage));
                setLoading(false);
                window.requestAnimationFrame(scrollToBottom);
            }
            if (!active) return [];
            return initialQueue.request(true, abortController.signal);
        }).then(() => {
            if (active) window.requestAnimationFrame(scrollToBottom);
        }).catch(loadError => {
            if (active && loadError?.name !== 'AbortError') setError(loadError);
        }).finally(() => active && setLoading(false));
        return () => {
            active = false;
            initialQueue.cancel();
            abortController.abort();
        };
    }, [conversationId, readConversationMessages, refreshMessages, scrollToBottom, owner]);

    const eventRefreshQueue = useMemo(() => createRefreshQueue(async (_, signal) => {
        const page = await refreshMessages(true, signal);
        if (atBottomRef.current) window.requestAnimationFrame(scrollToBottom);
        else setNewMessageNotice(true);
        if (page.length === 50) queueMicrotask(() => { void eventRefreshQueue.request(true).catch(setError); });
        return page;
    }, () => true, {minIntervalMs: 200, retry: true}), [refreshMessages, scrollToBottom]);
    useEffect(() => () => eventRefreshQueue.cancel(), [eventRefreshQueue]);
    useEffect(() => {
        // Catch-up can arrive before the initial page. Let that page establish
        // pagination first, then drain the latest merged invalidation.
        if (initialSnapshotOwner === owner && invalidation && conversationId) void eventRefreshQueue.request(true).catch(refreshError => {
            if (refreshError?.name !== 'AbortError') setError(refreshError);
        });
    }, [invalidation, conversationId, eventRefreshQueue, initialSnapshotOwner, owner]);

    const refreshChangedMessage = useMessageMutations({events: messageMutationEvents, messages, messagesRef, setMessages, setError, owner, conversationId, actorId: currentUser?.id});

    useEffect(() => {
        if (!conversationId || !atBottom || document.visibilityState !== 'visible' || highest <= serverRead) return;
        void acknowledge(highest);
    }, [conversationId, atBottom, highest, serverRead, acknowledge]);

    useEffect(() => {
        const retryRead = () => {
            if (document.visibilityState === 'visible' && atBottomRef.current) {
                if (messagesScope.current === activeScope.current) void acknowledge(highestServerSequence(messagesRef.current), {retry: true});
            }
        };
        document.addEventListener('visibilitychange', retryRead);
        window.addEventListener('chat-refresh-read', retryRead);
        return () => {
            document.removeEventListener('visibilitychange', retryRead);
            window.removeEventListener('chat-refresh-read', retryRead);
        };
    }, [acknowledge]);

    useEffect(() => {
        if (!conversationId || messagesScope.current !== owner || !messages.length || !cacheConversationMessages) return undefined;
        const timer = window.setTimeout(() => {
            void cacheConversationMessages(conversationId, messages.filter(isServerMessage));
        }, 150);
        return () => window.clearTimeout(timer);
    }, [cacheConversationMessages, conversationId, messages, owner]);

    const loadOlder = useCallback(async () => {
        const first = messages.find(isServerMessage)?.sequence;
        if (!conversationId || !first) return;
        const timeline = timelineRef.current;
        const oldHeight = timeline?.scrollHeight || 0;
        try {
            const payload = await listMessages(conversationId, {beforeSequence: first});
            if (activeScope.current !== owner || !owner.mounted) return;
            const older = payload.messages || [];
            setMessages(current => mergeMessages(older, current));
            setHasMore(older.length === 50);
            window.requestAnimationFrame(() => {
                if (timeline) timeline.scrollTop += timeline.scrollHeight - oldHeight;
            });
        } catch (loadError) {
            if (activeScope.current === owner && owner.mounted) setError(loadError);
        }
    }, [conversationId, messages, owner]);

    const sendMessage = useCallback(async (body, attachments = [], quotedMessage = null, retryId, originalBody) => {
        if (!conversationId || !currentUser) return;
        const clientId = retryId || crypto.randomUUID();
        const result = await sendOutbox({body, attachments, quotedMessage, clientId, originalBody}, () => createMessage(
            conversationId, body, clientId, attachments.map(attachment => attachment.id), quotedMessage?.id || null, originalBody,
        ));
        if (!result) return;
        setMessages(current => mergeMessages(current, [result.message]));
        window.requestAnimationFrame(scrollToBottom);
    }, [conversationId, currentUser, sendOutbox, scrollToBottom]);

    const changeMessage = useCallback(async (message, action) => {
        try {
            await action();
            await refreshChangedMessage(message.id);
        } catch (actionError) {
            setError(actionError);
            throw actionError;
        }
    }, [refreshChangedMessage]);

    const actions = useMemo(() => ({
        edit: (message, body) => changeMessage(message, () => updateMessage(message.id, body, message.version)),
        remove: message => changeMessage(message, () => deleteMessage(message.id)),
        react: (message, emoji, reacted) => changeMessage(message, () => reacted ? removeReaction(message.id, emoji) : addReaction(message.id, emoji)),
        retry: message => sendMessage(message.body, message.attachments || [], message.quoted_message || null, message.client_message_id, message.original_body),
    }), [changeMessage, sendMessage]);

    const handleScroll = useCallback(event => {
        const element = event.currentTarget;
        const bottom = element.scrollHeight - element.scrollTop - element.clientHeight < 80;
        atBottomRef.current = bottom;
        setAtBottom(bottom);
        if (bottom) setNewMessageNotice(false);
    }, []);

    const visibleMessages = useMemo(() => [...messages, ...outbox.messages], [messages, outbox.messages]);
    return {
        messages: visibleMessages,
        storageWarning: outbox.warning,
        loading,
        hasMore,
        error,
        setError,
        newMessageNotice,
        timelineRef,
        refreshMessages,
        loadOlder,
        sendMessage,
        actions,
        handleScroll,
        scrollToBottom,
    };
}
